import { useEffect, useContext } from 'react'

import {
  Button, Form, LoadingModule, ModalContext, SidebarModal, SidebarNoticeBox, Text,
} from '@domain-group/suit-theme'

import { digObject } from '@domain-group/javascript-utils'

import type { HandleCallbackActionParams } from '@functions/handleCallbackAction'

import BrowseComparables from './components/BrowseComparables'
import DataProviderBlankState from './components/DataProviderBlankState'
import PropertySuggestion from './components/PropertySuggestion'
import SetPropertyMatch from './components/SetPropertyMatch'

import useBrowseCampaignComparablesModal from './hooks/useBrowseCampaignComparablesModal'

const MODAL_KEY = 'BrowseCampaignComparablesModal'

export type ModalCallbacks = {
  closeModal: () => void,
  createCampaignComparable: (payload: HandleCallbackActionParams) => void,
  resetCampaignDataProvider: (payload: HandleCallbackActionParams) => void,
  updateCampaignDataProvider: (payload: HandleCallbackActionParams) => void,
}

type ModalProps = {
  callbacks: ModalCallbacks,
  showModal: boolean,
}

const BrowseCampaignComparablesModal = (props: ModalProps) => {
  const { callbacks, showModal } = props
  const { resetCampaignDataProvider } = callbacks || {}

  const modalContext = useContext(ModalContext)
  const { modalData } = modalContext

  const { campaignComparable } = digObject(modalData, MODAL_KEY, {})

  const hookPayload = useBrowseCampaignComparablesModal({ campaignComparable })

  const {
    agencyDataProviderTitle,
    disableBrowse,
    callbacks: {
      browseComparables,
      fetchPropertyMatchSuggestions,
      resetCampaignDataProvider: resetFn,
    },
    hasFilteredBrowseResults,
    hasPropertyMatchSuggestions,
    isMatchedWithDataProvider,
    propertyMatchSuggestions,
    searchingBrowseComparables,
    searchingForPropertyMatch,
  } = hookPayload

  useEffect(() => {
    if (!isMatchedWithDataProvider) fetchPropertyMatchSuggestions({ autoSelect: true })
  }, [])

  const resetCampaignDataProviderPayload = {
    callbacks: {
      action: resetFn,
      afterAction: fetchPropertyMatchSuggestions,
    },
    toastText: 'Data Match Reset',
  }

  return (
    <SidebarModal callbacks={callbacks} clickSafeZone modalKey={MODAL_KEY} showModal={showModal}>
      <SidebarModal.Header callbacks={callbacks} title="Add" titleSecondLine="Comparable" />

      <SidebarModal.Content>
        <LoadingModule loading={searchingForPropertyMatch} times={3} />

        {!searchingForPropertyMatch && (
          <>
            {/* BlankState */}
            {!hasPropertyMatchSuggestions && !isMatchedWithDataProvider && (
              <DataProviderBlankState title={agencyDataProviderTitle} />
            )}

            {/* Is not matched with Data Provider and there are suggestions */}
            {hasPropertyMatchSuggestions && !isMatchedWithDataProvider && (
              <>
                <SidebarNoticeBox>
                  <SidebarNoticeBox.Title>Confirm your Property</SidebarNoticeBox.Title>

                  <SidebarNoticeBox.Paragraph>
                    In order to search for similar features to your property, you&apos;ll need to match the property
                    you&apos;re working on with {agencyDataProviderTitle || 'your data supplier'}&apos;s property
                    identity code.
                  </SidebarNoticeBox.Paragraph>

                  <SidebarNoticeBox.Paragraph>
                    Please select the correct property from the list of properties below.
                  </SidebarNoticeBox.Paragraph>
                </SidebarNoticeBox>

                <Text
                  color="bodyFontLightColor"
                  fontSize="xsmall"
                  marginBottom="5px"
                >
                  Available Properties
                </Text>

                {propertyMatchSuggestions.map(suggestion => (
                  <PropertySuggestion
                    key={suggestion.id}
                    callbacks={callbacks}
                    hookPayload={hookPayload}
                    suggestion={suggestion}
                  />
                ))}
              </>
            )}

            {/* Is matched with Data Provider */}
            {isMatchedWithDataProvider && (
              <BrowseComparables
                callbacks={callbacks}
                hookPayload={hookPayload}
              />
            )}

            <SidebarModal.Separator boxProps={{ marginTop: !hasFilteredBrowseResults ? 'large' : null }} />

            {/*
             // @ts-expect-error TS2741 */}
            <Form.Field marginTop="large" label="More Options" />

            {/* Is not matched with Data Provider and the user wants to manually set the ID */}
            {!isMatchedWithDataProvider && <SetPropertyMatch hookPayload={hookPayload} />}

            {/* Unmatch the Data Provider and start again */}
            {isMatchedWithDataProvider && (
              <Button
                buttonStyle="secondaryEdit"
                loading={searchingForPropertyMatch}
                // @ts-expect-error TS2345
                onClick={() => resetCampaignDataProvider(resetCampaignDataProviderPayload)}
                size="medium"
              >
                Reset Property Match
              </Button>
            )}
          </>
        )}
      </SidebarModal.Content>

      <SidebarModal.Footer>
        <Button
          buttonStyle="primaryCreate"
          disabled={disableBrowse}
          loading={searchingBrowseComparables}
          onClick={() => browseComparables()}
          size="large"
        >
          Browse Results
        </Button>
      </SidebarModal.Footer>
    </SidebarModal>
  )
}

const LazyLoadedModal = (props: ModalProps) => (
  <SidebarModal.RenderController {...props}>
    <BrowseCampaignComparablesModal {...props} />
  </SidebarModal.RenderController>
)

export default LazyLoadedModal
